import { useMemo } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { Validate } from 'react-hook-form';
import { Job } from 'src/models/jobs/job.model';
import { useCombinedFormErrors } from '../../hooks/combined-form-errors.hook';
import { RangeInputGroupButton } from '../forms/controls/range-input-group/RangeInputGroupButton';
import { rangeInputGroupHelper } from '../forms/controls/range-input-group/range-input-group.constants';
import { useRangeInput } from '../forms/controls/range-input-group/range-input-group.hook';
import { HookControlProps } from '../forms/hook-form.models';
import { ControlFormGroup } from '../forms/shared/ControlFormGroup';

type RelevantFields = Pick<Job, 'positionsAvailableFrom' | 'positionsAvailableTo'>;

const relevantKeys: Readonly<(keyof RelevantFields)[]> = [
    'positionsAvailableFrom',
    'positionsAvailableTo',
] as const;

const validate: Validate<number | undefined, RelevantFields> = (_, formValues) => {
    const { positionsAvailableFrom, positionsAvailableTo } = formValues;

    return rangeInputGroupHelper.validate(positionsAvailableFrom, positionsAvailableTo);
};

type Props = Omit<HookControlProps, 'name'> & {
    initialFrom: number | undefined;
    initialTo: number | undefined;
};

export const AvailablePositionsControl = (props: Props): React.ReactElement => {
    const fieldError = useCombinedFormErrors<RelevantFields>(relevantKeys);

    const inputErrorClass = useMemo(() => {
        return fieldError ? 'border-danger' : '';
    }, [fieldError]);

    const { showRange, onRangeClick, register } = useRangeInput<RelevantFields>(
        props.initialFrom,
        props.initialTo,
        'positionsAvailableFrom',
        'positionsAvailableTo'
    );

    return (
        <ControlFormGroup
            {...props}
            name="__availablePositions"
            popover={props.popover}
            label={props.label}
            overrideError={fieldError}
        >
            <InputGroup>
                <Form.Control
                    {...register('positionsAvailableFrom', { validate })}
                    placeholder={showRange ? 'From' : ''}
                    className={inputErrorClass}
                    type="number"
                />
                {showRange && (
                    <Form.Control
                        placeholder="To"
                        {...register('positionsAvailableTo')}
                        className={inputErrorClass}
                        type="number"
                    />
                )}
                <RangeInputGroupButton onClick={onRangeClick} show={showRange} />
            </InputGroup>
        </ControlFormGroup>
    );
};
